/* eslint-disable no-nested-ternary */
/* global PLATFORM, G */
import { curry, getFirstItem, isArr, isStr } from 'lib/util'
import redirectSequence from '@gaia/sequence/module/adapter/router/redirect'
import routeComposition from '@gaia/trait/composition/route'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import { get } from 'lib/sequence/component/state/value'

/**
 * A mapping between a doc type and the appropriate route defined in the component
 *
 * @type {{docChunk: string, documentation: string}}
 */
const typeRouteMapping = {
  docChunk: 'attachment',
  documentation: 'attachment',
}

/**
 * A mapping between a docType and a callback that will return the appropriate args
 * for the doc type.
 *
 * @type {{docChunk: function(*): {value: {}}, documentation: function(*): {value: {}}}}
 */
const typeArgsMapping = {
  docChunk: value => ({
    value: {
      ...value?.page && { page: value.page },
      ...value?.label && { label: value.label },
      ...value?.documentationId && { documentationId: value.documentationId },
      ...value?.attachment && {
        attachment: { key: value?.attachment?.id, ...value?.attachment.attachment },
      },
    },
  }),
  documentation: value => ({
    value: {
      ...value?.label && { label: value.label },
      ...value?.url && { attachment: { value: { url: value.url, name: value.label } } },
      ...value?.documentationId && { documentationId: value.documentationId },
      ...value?.attachment && {
        attachment: { key: value?.attachment?.id, ...value?.attachment.attachment },
      },
    },
  }),
}

/**
 * Gets the correct doc type from the item.
 *
 * @param {Object} item
 * @returns {*|null}
 * @private
 */
const _getDocType = item => item?.meta?.type ?? item.metatype ?? item?.value?.docType ?? null

/**
 * Returns the appropriate route for the doc type.
 *
 * @param {Object} item
 * @param {Gaia.Component.Spec} component
 * @returns {{route, search: {}}}
 * @private
 */
const _getRoute = (item, component) => {
  const type = _getDocType(item)
  const { routes = {}, route = {} } = component[G.CONFIGURATION]
  const mappedType = typeRouteMapping?.[type] ?? type

  return {
    route: routes?.[mappedType] || route || {},
    search: route,
  }
}

/**
 * Returns the current search term in the {@link AppBar}'s search field.
 *
 * TODO: Improve access to search term, event?
 *       Look into capabilities to talk to the appbar search bar
 *
 * @param {Gaia.AppModule.Spec} module  app module
 * @returns {string|null}
 * @private
 */
const _getSearchBarTerm = (module) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { tabs } = sequenceComponentFindProxy(actionComponent)
  if (tabs) {
    const lists = tabs ? tabs[G.CHILDREN].reduce((acc, tab) => {
      const tabLists = tab[G.CHILDREN].filter(list => list._name === 'list'
          || (!list[G.STATE].hidden && list[G.PROPS].list))
      return [...acc, ...tabLists]
    }, []) : []

    return lists?.[0]?.[G.STATE]?.[G.META]?.term || null
  }

  return null
}

/**
 * Returns the correct search term based on the platform.
 * @param {Gaia.AppModule.Spec} module
 * @returns {*|null|string}
 * @private
 */
const _getSearchTerm = (module) => {
  if (PLATFORM === 'web') {
    const { searchField } = sequenceComponentFindProxy(module[G.STATE][G.ACTION][G.COMPONENT])
    return searchField ? get(searchField) : null
  }

  if (PLATFORM === 'mobile') {
    return _getSearchBarTerm(module)
  }

  return null
}

/**
 * Returns the appropriate args based on the doc type
 * @param {string} term         the search term
 * @param {Object} item         the selected item
 * @param {string} item.key     key of the item
 * @param {Object} item.value   value of the item
 * @param {string} item.type    doc type of the item
 * @returns {{[p: string]: *}|{key: *, value: *, term: *}}
 * @private
 */
const _getArgs = (term, { key, value, type }) => {
  const attachment = value?.attachment
    ? {
      key: value?.attachment?.id,
      ...value?.attachment.attachment,
    }
    : null

  const typeArgs = typeArgsMapping?.[type]?.(value)

  return typeArgs
    ? { ...typeArgs, attachment, term }
    : { key, value, term }
}

/**
 * Redirect To Part Or Equipment Event Handler
 *
 * Redirects to the appropriate route defined in `routes` of the calling component
 * based on the doc type of the selected item or to `route`, which is the fallback
 * search results route.
 *
 * @param {Gaia.AppModule.Spec} module      app module
 * @param {Gaia.Component.Spec} component   action component
 * @param {PlatformEvent} event             the event that triggered this handler
 * @param {Object} event.detail             custom event details
 * @param {string} event.detail.key         a model key to pass to {@link redirectSequence}
 * @returns {Promise<Event | undefined>} void
 */
const redirectToSearchOrDetail = async (module, component, event) => {
  const moduleState = module[G.STATE]

  try {
    const { item, options = [], term } = event.detail || {}
    const { route: docRoute, search: searchRoute } = _getRoute(item, component)

    const pressedEnterKey = event.key === 'Enter'
    const pressedSearchIcon = !pressedEnterKey && (!item || isStr(item) || isArr(item))

    // If item is null, the user deleted (backspace) the search term, we need to
    // exit early in this case.
    if (item === null || (isArr(item) && pressedEnterKey)) {
      return
    }

    const soleCandidate = options?.length === 1 && options?.[0]?.key !== -1

    if ((item && isStr(item) && !soleCandidate) || pressedSearchIcon) {
      moduleState[G.ROUTE] = routeComposition(searchRoute.module, searchRoute.action)

      const itemTerm = (isStr(item) && item.trim())
        || (isArr(item) && !item?.length && null)
        || (isArr(item) && item?.length && (item[0]?.value || item[0]))
      await redirectSequence(module)(item ? { term: itemTerm } : [])
      return
    }

    const candidate = soleCandidate ? getFirstItem(options) : item
    const { key, value } = candidate || {}

    const docType = _getDocType(candidate)
    const searchTerm = _getSearchTerm(module)
    const args = _getArgs(term || searchTerm, { key, value, type: docType })

    moduleState[G.ROUTE] = routeComposition(docRoute.module, docRoute.action)

    await redirectSequence(module)(args)
  } catch (e) {
    console.error(e)
  }
}

export default curry(redirectToSearchOrDetail)
